import React, { CSSProperties, ReactNode, useContext } from 'react';
import {
  MenuItem as MUIMenuItem,
  MenuItemProps as MUIMenuItemProps,
  SxProps,
} from '@mui/material';

import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

import { MenuContext } from './Menu';

type CustomProps = {
  /**
   * whether to close the parent Menu on click
   * defaults to true
   */
  closeMenuOnClick?: boolean;
  /**
   * optional node to render at the start of the menu item
   */
  startAdornment?: ReactNode;
  /**
   * optional styles to apply to the container of the given startAdornment
   */
  startAdornmentStyle?: CSSProperties;
};

type AtomMUIProps = {
  children?: ReactNode;
  className?: string;
  classes?: any;
  onClick?: (event: any) => void;
  disabled?: boolean;
  selected?: boolean;
};

type MenuItemProps = CustomProps & AtomMUIProps;

const sx: SxProps = {
  minHeight: '2.25rem',
  fontWeight: 500,
  fontSize: fonts.md,
  display: 'flex',
  justifyContent: 'flex-start',
  paddingLeft: '16px',
  paddingRight: '16px',
  width: 'auto',
  '&:focus-visible': {
    outline: 'none',
  },
  '&:selected': {
    color: colors.brand.blue,
  },
};

const styles = {
  startAdornment: {
    marginRight: '1rem',
  },
};

// forwardRef needed to forward ref passed by Menu component
const MenuItem = React.forwardRef(
  (props: MenuItemProps, ref: React.Ref<any>) => {
    const { open, close } = useContext(MenuContext);

    const {
      closeMenuOnClick = true,
      children,
      startAdornment,
      startAdornmentStyle = {},
      ...data
    } = props;

    const MUIProps: MUIMenuItemProps = data;

    const handleClick = (
      event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    ) => {
      if (props.onClick) {
        props.onClick(event);
      }

      if (open && closeMenuOnClick) {
        close(event);
      }
    };

    return (
      <MUIMenuItem {...MUIProps} onClick={handleClick} sx={sx} ref={ref}>
        {startAdornment && (
          <div style={{ ...styles.startAdornment, ...startAdornmentStyle }}>
            {startAdornment}
          </div>
        )}
        {children}
      </MUIMenuItem>
    );
  },
);

export default MenuItem;
